import React, { useEffect } from 'react';
import PropTypes, {
  arrayOf,
  func,
  number,
  shape,
  string,
} from 'prop-types';
import { Link } from '@resy/ui-kitchen';
import AmexMarketingEditorialsCard from './AmexMarketingEditorialsCard';
import { sanitizeString } from '../../../../../../ui-kitchen/src/utils/sanitizeString';
import './AmexMarketingEditorials.scss';

const AmexMarketingEditorials = (
  {
    editorialsImagePath,
    editorialsSectionId,
    editorialData: {
      title,
      subtitle_1: subtitlePrefix,
      subtitle_2: subtitleSuffix,
      contents,
      subtitle_link: subtitleLink = {},
      max_editorials: maxEditorials = 1,
      editorials_per_row_mobile: maxEditorialsPerRowMobile = 1,
      utm,
    },
    Analytics,
    getFilteredEditorialsListLength,
    componentCallback,
  }
) => {
  const calculateTotalEditorials = () => {
    if (maxEditorials.length === 0) return 1;
    if (maxEditorials > 4) return 4;
    return maxEditorials;
  };
  const requiredKeys = ['id', 'image', 'title', 'url'];
  const totalEditorials = calculateTotalEditorials();
  const totalEditorialsPerRowMobile = (maxEditorialsPerRowMobile.length === 0 || maxEditorialsPerRowMobile > 2) ? 1 : maxEditorialsPerRowMobile;

  const filteredCards = contents
    .filter((item) => requiredKeys
      .every((i) => Object.prototype.hasOwnProperty.call(item, i) && item[i]))
    .slice(0, totalEditorials);
  const hasSubtitleLink = subtitleLink?.label && subtitleLink?.url;
  const hasSubtitleContent = hasSubtitleLink || subtitlePrefix || subtitleSuffix;
  // check if after filtering editorials list length is less than totalEditorials
  const maxCards = filteredCards.length < totalEditorials ? filteredCards.length : totalEditorials;

  useEffect(() => {
    getFilteredEditorialsListLength(filteredCards.length);
    if (componentCallback) componentCallback('isEditorialsDataLoaded', true);
  }, [filteredCards.length]);

  const subtitleLinkUtm = subtitleLink?.utm;

  const renderEditorials = () => (
    filteredCards.map((post) => (
      <AmexMarketingEditorialsCard
        editorialsImagePath={editorialsImagePath}
        key={`post_${post.id}${post.title}`}
        Analytics={Analytics}
        post={post}
        utm={utm}
      />
    ))
  );

  const displayCTA = (cta) => {
    const { label, url } = cta;

    return (
      <Link
        className="AmexMarketingEditorials__text-link"
        href={`${url}${!subtitleLinkUtm ? '' : subtitleLinkUtm}`}
        target="_blank"
        key={label}
      >
        { label }
      </Link>
    );
  };

  const sanitizedTitle = sanitizeString(title);
  const sanitizedSubtitlePrefix = sanitizeString(subtitlePrefix);
  const sanitizedSubtitleSuffix = sanitizeString(subtitleSuffix);

  if (filteredCards.length > 0) {
    return (
      <section className="AmexMarketingEditorials">
        <div className="container">
          <div className="AmexMarketingEditorials__wrapper">
            <h2
              id={editorialsSectionId}
              className="AmexMarketingEditorials__title"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: sanitizedTitle,
              }}
            />
            {hasSubtitleContent && (
              <p className="AmexMarketingEditorials__subtitle">
                <span
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: sanitizedSubtitlePrefix,
                  }}
                />
                {hasSubtitleLink && displayCTA(subtitleLink)}
                <span
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: sanitizedSubtitleSuffix,
                  }}
                />
              </p>
            )}
            <ul
              className={`AmexMarketingEditorials__list max-cards-desktop-${maxCards} max-cards-mobile-${totalEditorialsPerRowMobile}`}
            >
              {renderEditorials()}
            </ul>
          </div>
        </div>
      </section>
    );
  }
  return null;
};

AmexMarketingEditorials.propTypes = {
  editorialsImagePath: string.isRequired,
  editorialsSectionId: string.isRequired,
  editorialData: shape({
    title: string.isRequired,
    subtitle_1: string,
    subtitle_2: string,
    subtitle_link: shape({}),
    contents: arrayOf(shape({})),
    max_editorials: number.isRequired,
    editorials_per_row_mobile: number.isRequired,
    utm: string.isRequired,
  }),
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  getFilteredEditorialsListLength: func,
  componentCallback: PropTypes.func,
};

AmexMarketingEditorials.defaultProps = {
  editorialData: {
    text_1: '',
    text_2: '',
    text_link: {},
    contents: [],
  },
  getFilteredEditorialsListLength: () => {},
  componentCallback: () => {},
};

export default AmexMarketingEditorials;
